import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUserStore } from '@/store/useUserStore';
import { usePointAccount } from '@/hooks/usage/usePointAccount';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/api/track';
import { useStudioStore } from '@/store/useStudioStore';
import { CREDIT_PURCHASE_URL, openCreditPurchase } from '@/lib/creditPurchase';

type AccountMenuVariant = 'default' | 'autopilot' | 'diagnosis';
type AccountMenuTriggerVariant = 'default' | 'compact' | 'walkthrough' | 'studio';

const getErrorCode = (error: unknown) => {
  const maybeError = error as {
    code?: string | number;
    message?: string;
    response?: { status?: number; data?: { code?: string | number; message?: string } };
  };
  return String(
    maybeError.response?.data?.code ?? maybeError.response?.status ?? maybeError.code ?? 'unknown',
  );
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  const maybeError = error as { message?: string; response?: { data?: { message?: string } } };
  return maybeError.response?.data?.message || maybeError.message || '';
};

/**
 * UserAccountMenu 组件
 * 展示用户信息、余额并提供导航选项，鼠标悬停展开菜单
 */
export const UserAccountMenu = ({
  triggerClassName,
  menuVariant = 'default',
  showDashboardLink,
  triggerVariant = 'default',
}: {
  triggerClassName?: string;
  menuVariant?: AccountMenuVariant;
  showDashboardLink?: boolean;
  triggerVariant?: AccountMenuTriggerVariant;
}) => {
  const { t } = useTranslation('common');
  const { user, logout, loginSource, fetchUserInfo } = useUserStore();
  const { balance, loading: balanceLoading } = usePointAccount({
    trackingSource: 'account_menu_balance',
  });
  const setCreditBalanceState = useStudioStore((state) => state.setCreditBalanceState);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const openRef = useRef(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const isBalanceNegative = typeof balance === 'number' && balance < 0;
  const showNegativeBalanceEffect = isBalanceNegative;

  useEffect(() => {
    if (triggerVariant !== 'studio') return;
    setCreditBalanceState(balance, balanceLoading);
  }, [balance, balanceLoading, setCreditBalanceState, triggerVariant]);

  // OAuth 登录后 name 为空时，组件挂载后拉取真实用户信息
  useEffect(() => {
    if (loginSource === 'oauth' && user && !user.name) {
      fetchUserInfo();
    }
  }, [loginSource, user, fetchUserInfo]);

  const cancelClose = () => clearTimeout(closeTimer.current);

  if (!user) return null;

  const route = `${location.pathname}${location.search}${location.hash}`;
  const isUsage = location.pathname === '/usage';
  const isCredits = location.pathname === '/credits';
  const isStudio = location.pathname === '/studio';
  const shouldShowDashboardLink = showDashboardLink ?? isStudio;
  const isAutopilotVariant = menuVariant === 'autopilot' || menuVariant === 'diagnosis';
  const showCompactTrigger = triggerVariant === 'compact' || Boolean(triggerClassName);
  const showStudioTrigger = triggerVariant === 'studio';
  const showWalkthroughTrigger = triggerVariant === 'walkthrough';

  const trackAccountMenuOpen = () => {
    trackEvent({
      eventCode: 'account_menu_open',
      eventData: JSON.stringify({
        route,
        menuVariant,
        balanceVisible: true,
        balanceLoading,
        showDashboardLink: shouldShowDashboardLink,
      }),
    });
  };

  const setMenuOpen = (nextOpen: boolean) => {
    if (nextOpen && !openRef.current) {
      trackAccountMenuOpen();
    }
    openRef.current = nextOpen;
    setOpen(nextOpen);
  };

  const startClose = () => {
    closeTimer.current = setTimeout(() => setMenuOpen(false), 120);
  };

  const balanceDisplay = balanceLoading ? '...' : (balance?.toLocaleString('en-US') ?? '-');

  const trackAccountMenuItemClick = (itemKey: string, targetRoute: string) => {
    trackEvent({
      eventCode: 'account_menu_item_click',
      eventData: JSON.stringify({
        route,
        itemKey,
        targetRoute,
        menuVariant,
        balance,
      }),
    });
  };

  const handleMenuNavigate = (itemKey: string, targetRoute: string) => {
    trackAccountMenuItemClick(itemKey, targetRoute);
    navigate(targetRoute);
  };

  const handleLogout = async () => {
    const userId = user.id?.toString();
    trackAccountMenuItemClick('sign_out', '/login');

    try {
      await logout();
      trackEvent({
        eventCode: 'account_logout_result',
        userId,
        eventData: JSON.stringify({
          route,
          result: 'success',
          errorCode: null,
          errorMessage: null,
        }),
      });
      navigate('/login');
    } catch (error: unknown) {
      trackEvent({
        eventCode: 'account_logout_result',
        userId,
        eventData: JSON.stringify({
          route,
          result: 'failed',
          errorCode: getErrorCode(error),
          errorMessage: getErrorMessage(error),
        }),
      });
    }
  };

  const itemCls = isAutopilotVariant
    ? 'ap-user-menu-item'
    : 'text-[#64748B] cursor-pointer gap-[9px] rounded-[8px] px-[10px] py-2 text-sm font-medium';
  const activeItemCls = isAutopilotVariant
    ? 'ap-user-menu-item is-active'
    : 'bg-[#EEF2FF] text-[#4F46E5] cursor-pointer gap-[9px] rounded-[8px] px-[10px] py-2 text-sm font-semibold';
  const triggerBaseClassName = showStudioTrigger
    ? cn(
        'flex h-9 min-w-9 cursor-pointer items-center justify-center gap-[7px] rounded-full border bg-white py-[3px] pr-[5px] pl-[9px] shadow-sm transition-all',
        showNegativeBalanceEffect
          ? 'border-[rgba(220,38,38,0.22)] shadow-[0_0_0_3px_rgba(220,38,38,0.07)] hover:border-[rgba(220,38,38,0.34)]'
          : 'border-border hover:border-violet-700/[.34] hover:shadow-[0_0_0_3px_rgba(109,40,217,0.08)]',
        triggerClassName,
      )
    : showCompactTrigger
      ? cn(
          'flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border bg-white p-[2px] shadow-sm transition-all',
          showNegativeBalanceEffect
            ? 'border-[rgba(220,38,38,0.18)] hover:border-[rgba(220,38,38,0.34)] hover:shadow-[0_0_0_3px_rgba(220,38,38,0.08)]'
            : 'border-border hover:border-violet-700/[.34] hover:shadow-[0_0_0_3px_rgba(109,40,217,0.08)]',
          triggerClassName,
        )
      : showNegativeBalanceEffect
        ? 'border-[rgba(220,38,38,0.18)] bg-white hover:border-[rgba(220,38,38,0.34)] hover:shadow-[0_0_0_3px_rgba(220,38,38,0.08)] ml-1 flex cursor-pointer items-center gap-2 rounded-full border px-2 py-1.5 shadow-sm transition-all'
        : 'bg-card border-border hover:border-primary/30 ml-1 flex cursor-pointer items-center gap-2 rounded-full border px-2 py-1.5 shadow-sm transition-all';

  return (
    <DropdownMenu open={open} onOpenChange={setMenuOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <div
          onMouseEnter={() => {
            cancelClose();
            setMenuOpen(true);
          }}
          onMouseLeave={startClose}
          onClick={(e) => e.preventDefault()}
          className={triggerBaseClassName}
          style={showWalkthroughTrigger ? { padding: '0.375rem 0.5rem' } : undefined}
        >
          {showStudioTrigger ? (
            <>
              <span
                className={cn(
                  'inline-flex items-center gap-1 text-[11.5px] font-bold whitespace-nowrap',
                  showNegativeBalanceEffect ? 'text-[#B91C1C]' : 'text-[#64748B]',
                )}
                aria-label={`${balanceDisplay} credits`}
              >
                <i
                  className={cn(
                    'fa-solid fa-coins text-[12px]',
                    showNegativeBalanceEffect ? 'text-[#DC2626]' : 'text-[#D97706]',
                  )}
                  aria-hidden="true"
                />
                <strong
                  className={cn(
                    'font-bold tabular-nums',
                    showNegativeBalanceEffect ? 'text-[#B91C1C]' : 'text-[#0D0D1A]',
                  )}
                >
                  {balanceDisplay}
                </strong>
                <span
                  className={cn(
                    'text-[10.5px] font-semibold',
                    showNegativeBalanceEffect ? 'text-[#B91C1C]/75' : 'text-[#94A3B8]',
                  )}
                >
                  credits
                </span>
              </span>
              <Avatar className="h-[26px] w-[26px] border-0">
                {!showNegativeBalanceEffect && <AvatarImage src={user.icon} />}
                <AvatarFallback
                  className={cn(
                    'text-[11px] font-bold tracking-[0.02em] text-white',
                    showNegativeBalanceEffect
                      ? 'bg-[linear-gradient(135deg,#EF4444,#F97316)] shadow-[0_4px_12px_rgba(220,38,38,0.22)]'
                      : 'bg-gradient-to-br from-[#4F46E5] to-[#7C3AED]',
                  )}
                >
                  {user.name?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            </>
          ) : showCompactTrigger ? (
            <span
              className={cn(
                'flex h-full w-full items-center justify-center rounded-full text-[11px] font-bold tracking-[0.02em] text-white',
                showNegativeBalanceEffect
                  ? 'bg-[linear-gradient(135deg,#EF4444,#F97316)] shadow-[0_6px_16px_rgba(220,38,38,0.24)]'
                  : 'bg-gradient-to-br from-[#4F46E5] to-[#7C3AED]',
              )}
            >
              {user.name?.charAt(0).toUpperCase() || 'U'}
            </span>
          ) : (
            <>
              <Avatar
                className={cn(
                  'h-6 w-6 border border-white/20',
                  showNegativeBalanceEffect && 'border-0 after:border-0',
                )}
              >
                {!showNegativeBalanceEffect && <AvatarImage src={user.icon} />}
                <AvatarFallback
                  className={cn(
                    'text-[10px] font-bold text-white',
                    showNegativeBalanceEffect
                      ? 'bg-[linear-gradient(135deg,#EF4444,#F97316)] shadow-[0_6px_16px_rgba(220,38,38,0.24)]'
                      : 'bg-gradient-to-br from-indigo-500 to-purple-500',
                  )}
                >
                  {user.name?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div
                className={cn(
                  'flex items-center gap-1 text-[12px] font-bold',
                  showNegativeBalanceEffect ? 'text-[#B91C1C]' : 'text-foreground',
                )}
              >
                <i
                  className={cn(
                    'fa-solid fa-coins text-[12px]',
                    showNegativeBalanceEffect ? 'text-[#DC2626]' : 'text-amber-500',
                  )}
                />
                {balanceDisplay}
              </div>
            </>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className={cn(
          'w-56 rounded-xl',
          isAutopilotVariant && 'ap-user-menu',
          menuVariant === 'diagnosis' && 'is-diagnosis',
        )}
        style={{ zIndex: 9999 }}
        onMouseEnter={cancelClose}
        onMouseLeave={startClose}
      >
        <DropdownMenuLabel className={isAutopilotVariant ? 'ap-user-menu-label' : 'px-3 py-3'}>
          <div className="truncate text-sm font-bold">{user.name}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className={isAutopilotVariant ? 'ap-user-menu-separator' : ''} />

        <div
          className={cn(
            isAutopilotVariant
              ? 'ap-user-menu-credits'
              : showNegativeBalanceEffect
                ? 'm-1 flex items-center justify-between rounded-[11px] border border-[rgba(220,38,38,0.18)] bg-[linear-gradient(135deg,rgba(254,226,226,0.86),rgba(255,237,213,0.72))] px-3 py-2'
                : 'm-1 flex items-center justify-between rounded-lg bg-amber-50/50 px-3 py-2 dark:bg-amber-900/10',
            isAutopilotVariant && showNegativeBalanceEffect && 'is-negative',
          )}
        >
          <span
            className={
              isAutopilotVariant
                ? ''
                : showNegativeBalanceEffect
                  ? 'text-[11px] font-medium text-[rgba(13,13,26,0.56)]'
                  : 'text-[11px] font-semibold text-amber-700/70 dark:text-amber-500/70'
            }
          >
            {showNegativeBalanceEffect
              ? t('account_menu.balance_below_zero')
              : t('account_menu.credits_remaining')}
          </span>
          <span
            className={
              isAutopilotVariant
                ? ''
                : showNegativeBalanceEffect
                  ? 'flex items-center gap-1 text-[14px] font-extrabold text-[#B91C1C]'
                  : 'flex items-center gap-1 text-xs font-black text-amber-600'
            }
          >
            <i
              className={cn(
                'fa-solid fa-coins text-[10px]',
                showNegativeBalanceEffect ? 'text-[#DC2626]' : 'text-current',
              )}
            />
            {balanceDisplay}
          </span>
        </div>

        {shouldShowDashboardLink && (
          <DropdownMenuItem
            onClick={() => handleMenuNavigate('dashboard', '/dashboard')}
            className={itemCls}
          >
            <i className="fa-solid fa-layer-group w-[15px] text-center text-[12px]" />
            {t('Dashboard')}
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          onClick={() => handleMenuNavigate('usage_history', '/usage')}
          className={isUsage ? activeItemCls : itemCls}
        >
          <i className="fa-solid fa-clock-rotate-left w-[15px] text-center text-[12px]" />
          {t('account_menu.usage_history')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            trackAccountMenuItemClick('buy_credits', CREDIT_PURCHASE_URL);
            openCreditPurchase();
          }}
          className={isCredits ? activeItemCls : itemCls}
        >
          <i className="fa-solid fa-coins w-[15px] text-center text-[12px]" />
          {t('account_menu.buy_credits')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleMenuNavigate('help', '/help')} className={itemCls}>
          <i className="fa-regular fa-circle-question w-[15px] text-center text-[12px]" />
          {t('account_menu.help')}
        </DropdownMenuItem>

        <DropdownMenuSeparator className={isAutopilotVariant ? 'ap-user-menu-separator' : ''} />
        <DropdownMenuItem
          onClick={handleLogout}
          className={
            isAutopilotVariant
              ? 'ap-user-menu-item is-danger'
              : 'text-destructive cursor-pointer gap-[9px] rounded-[8px] px-[10px] py-2 text-[13px] font-semibold'
          }
        >
          <i className="fa-solid fa-right-from-bracket w-[15px] text-center text-[12px]" />
          {t('account_menu.sign_out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
